import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  endExamSession as endSessionApi,
  getActiveExamSession,
  logExamAuditEvent,
  startExamSession as startSessionApi,
  type ExamAuditEventType,
  type ExamSessionMeta,
} from '../lib/examMode';

interface ExamModeContextValue {
  /** The active session, or null when not in exam mode. */
  session: ExamSessionMeta | null;
  /** True when an exam attempt is currently underway. */
  active: boolean;
  /** Start a new locked exam attempt. Writes session_started to the audit log. */
  start: (input: {
    candidateName: string;
    candidateNumber: string;
    schoolName: string;
    paperTitle: string;
  }) => Promise<ExamSessionMeta>;
  /** End the active attempt and unlock. */
  end: (reason:
    | 'completed'
    | 'abandoned_by_candidate'
    | 'abandoned_by_invigilator'
    | 'time_expired'
    | 'technical_fault'
  ) => Promise<void>;
  /** Append an event to the audit log. No-ops when not in exam mode. */
  logEvent: (event: ExamAuditEventType, metadata?: Record<string, unknown>) => void;
}

const ExamModeContext = createContext<ExamModeContextValue | undefined>(undefined);

export function ExamModeProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<ExamSessionMeta | null>(() => getActiveExamSession());

  // Refresh from localStorage every time the tab gains focus, in case a separate tab
  // or a programmatic call changed the active session.
  useEffect(() => {
    const refresh = () => setSession(getActiveExamSession());
    window.addEventListener('focus', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const start = useCallback(
    async (input: Parameters<ExamModeContextValue['start']>[0]) => {
      const next = await startSessionApi(input);
      setSession(next);
      return next;
    },
    [],
  );

  const end = useCallback(
    async (reason: Parameters<ExamModeContextValue['end']>[0]) => {
      await endSessionApi({ endReason: reason });
      setSession(null);
    },
    [],
  );

  const logEvent = useCallback(
    (event: ExamAuditEventType, metadata?: Record<string, unknown>) => {
      // Fire and forget. The audit endpoint is best-effort; the server-side locked
      // flag enforces the actual rule.
      void logExamAuditEvent(event, metadata);
    },
    [],
  );

  return (
    <ExamModeContext.Provider
      value={{ session, active: session !== null, start, end, logEvent }}
    >
      {children}
    </ExamModeContext.Provider>
  );
}

export function useExamMode(): ExamModeContextValue {
  const ctx = useContext(ExamModeContext);
  if (!ctx) throw new Error('useExamMode must be used inside <ExamModeProvider>.');
  return ctx;
}
